// Debounced runner that drops stale results (MEDICATION_BATCH_DOSE_ADJUST_PLAN.md, UI rule 2).
// schedule() invalidates any in-flight result immediately, so a slow earlier response
// cannot mark the UI ready while a newer edit is still pending.

export function createLatestRunner<A extends unknown[], R>(opts: {
  delayMs: number;
  run: (...args: A) => Promise<R>;
  onStart: () => void;
  onResult: (r: R) => void;
  onError: (e: unknown) => void;
}): { schedule: (...args: A) => void; cancel: () => void } {
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  function invalidate(): void {
    generation += 1;
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  }

  return {
    schedule(...args: A) {
      invalidate();
      const gen = generation;
      timer = setTimeout(() => {
        if (gen !== generation) return;
        timer = undefined;
        opts.onStart();
        void Promise.resolve()
          .then(() => opts.run(...args))
          .then(
            (result) => {
              if (gen === generation) opts.onResult(result);
            },
            (error: unknown) => {
              if (gen === generation) opts.onError(error);
            }
          );
      }, opts.delayMs);
    },
    cancel() {
      invalidate();
    },
  };
}
